'use strict';

function _interopDefault (ex) { return (ex && (typeof ex === 'object') && 'default' in ex) ? ex['default'] : ex; }

var React = require('react');
var React__default = _interopDefault(React);
var _slicedToArray = _interopDefault(require('@babel/runtime/helpers/slicedToArray'));
var _toConsumableArray = _interopDefault(require('@babel/runtime/helpers/toConsumableArray'));
var web = require('@react-spring/web');
var reactUseGesture = require('react-use-gesture');
var clamp = _interopDefault(require('lodash.clamp'));
var _defineProperty = _interopDefault(require('@babel/runtime/helpers/defineProperty'));
var merge = _interopDefault(require('lodash.merge'));
var _classCallCheck = _interopDefault(require('@babel/runtime/helpers/classCallCheck'));
var _createClass = _interopDefault(require('@babel/runtime/helpers/createClass'));
var _possibleConstructorReturn = _interopDefault(require('@babel/runtime/helpers/possibleConstructorReturn'));
var _getPrototypeOf = _interopDefault(require('@babel/runtime/helpers/getPrototypeOf'));
var _assertThisInitialized = _interopDefault(require('@babel/runtime/helpers/assertThisInitialized'));
var _inherits = _interopDefault(require('@babel/runtime/helpers/inherits'));
var ReactDOM = _interopDefault(require('react-dom'));

/**
 * Calculates the the translate(x,y) coordinates needed to zoom-in
 * to a point in an image.
 *
 * @param {ref} imageRef The image dom node used as a reference to calculate translate offsets
 * @param {number} scale The current transform scale of image
 * @param {number} pinchDelta The amount of change in the new transform scale
 * @param {array} touchOrigin The [x,y] coordinates of the zoom origin
 * @param {array} currentTranslate The current [x,y] translate values of image
 *
 * @returns {array} The next [x,y] translate values to apply to image
 */
var getTranslateOffsetsFromScale = function getTranslateOffsetsFromScale(_ref) {
  var imageRef = _ref.imageRef,
      scale = _ref.scale,
      pinchDelta = _ref.pinchDelta,
      _ref$touchOrigin = _slicedToArray(_ref.touchOrigin, 2),
      touchOriginX = _ref$touchOrigin[0],
      touchOriginY = _ref$touchOrigin[1],
      _ref$currentTranslate = _slicedToArray(_ref.currentTranslate, 2),
      translateX = _ref$currentTranslate[0],
      translateY = _ref$currentTranslate[1];

  var _imageRef$current$get = imageRef.current.getBoundingClientRect(),
      imageTopLeftY = _imageRef$current$get.top,
      imageTopLeftX = _imageRef$current$get.left,
      imageWidth = _imageRef$current$get.width,
      imageHeight = _imageRef$current$get.height; // Get the (x,y) touch position relative to image origin at the current scale


  var imageCoordX = (touchOriginX - imageTopLeftX - imageWidth / 2) / scale;
  var imageCoordY = (touchOriginY - imageTopLeftY - imageHeight / 2) / scale; // Calculate translateX/Y offset at the next scale to zoom to touch position

  var newTranslateX = -imageCoordX * pinchDelta + translateX;
  var newTranslateY = -imageCoordY * pinchDelta + translateY;
  return [newTranslateX, newTranslateY];
};

/**
 * Determines if the provided image is within the viewport
 *
 * @param {ref} imageRef The image dom node to measure against the viewport
 *
 * @returns {boolean} True if image needs to be resized to fit viewport, otherwise false
 */
var imageIsOutOfBounds = function imageIsOutOfBounds(imageRef) {
  var _imageRef$current$get = imageRef.current.getBoundingClientRect(),
      topLeftY = _imageRef$current$get.top,
      topLeftX = _imageRef$current$get.left,
      bottomRightY = _imageRef$current$get.bottom,
      bottomRightX = _imageRef$current$get.right;

  var _window = window,
      windowHeight = _window.innerHeight,
      windowWidth = _window.innerWidth;
  if (topLeftX > windowWidth * (1 / 2) || topLeftY > windowHeight * (1 / 2) || bottomRightX < windowWidth * (1 / 2) || bottomRightY < windowHeight * (1 / 2)) return true;
  return false;
};

/**
 * React Hook that returns the current window size
 * and report updates from the 'resize' window event
 *
 * @param {node} ref Dom node to watch for double clicks
 * @param {number} [latency=300] The amount of time (in milliseconds) to wait before differentiating a single from a double click
 * @param {function} onSingleClick A callback function for single click events
 * @param {function} onDoubleClick A callback function for double click events
 */

var useDoubleClick = function useDoubleClick(_ref) {
  var ref = _ref.ref,
      _ref$latency = _ref.latency,
      latency = _ref$latency === void 0 ? 300 : _ref$latency,
      _ref$onSingleClick = _ref.onSingleClick,
      onSingleClick = _ref$onSingleClick === void 0 ? function () {
    return null;
  } : _ref$onSingleClick,
      _ref$onDoubleClick = _ref.onDoubleClick,
      onDoubleClick = _ref$onDoubleClick === void 0 ? function () {
    return null;
  } : _ref$onDoubleClick;
  React.useEffect(function () {
    var clickRef = ref.current;
    var clickCount = 0;

    var handleClick = function handleClick(e) {
      clickCount += 1;
      setTimeout(function () {
        if (clickCount === 1) onSingleClick(e);else if (clickCount === 2) onDoubleClick(e);
        clickCount = 0;
      }, latency);
    }; // Add event listener for click events


    clickRef.addEventListener('click', handleClick); // Remove event listener

    return function () {
      clickRef.removeEventListener('click', handleClick);
    };
  });
};

/**
 * React Hook that returns the current window size
 * and report updates from the 'resize' window event
 *
 * @typedef {WindowSize} WindowSize
 * @property {number} width Window width
 * @property {number} height Window height
 * @returns {WindowSize} An object container the window width and height
 */

var useWindowSize = function useWindowSize() {
  var _useState = React.useState({
    width: window.innerWidth,
    height: window.innerHeight
  }),
      _useState2 = _slicedToArray(_useState, 2),
      windowSize = _useState2[0],
      setWindowSize = _useState2[1];

  React.useEffect(function () {
    var handleResize = function handleResize() {
      setWindowSize({
        width: window.innerWidth,
        height: window.innerHeight
      });
    }; // Add event listener for window resize events


    window.addEventListener('resize', handleResize); // Remove event listener

    return function () {
      window.removeEventListener('resize', handleResize);
    };
  });
  return windowSize;
};

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys(source, true).forEach(function (key) { _defineProperty(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys(source).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }
/**
 * Animates pinch-zoom + panning on image using spring physics
 *
 * @param {string} src The source URL of this image
 * @param {string} alt The alt attribute for this image
 * @param {boolean} isCurrentImage True if this image is currently shown in pager, otherwise false
 * @param {function} setDisableDrag Function that can be called to disable dragging in the pager
 *
 * @see https://github.com/react-spring/react-use-gesture
 * @see https://github.com/react-spring/react-spring
 */

var Image = function Image(_ref) {
  var src = _ref.src,
      alt = _ref.alt,
      isCurrentImage = _ref.isCurrentImage,
      setDisableDrag = _ref.setDisableDrag;
  var imageRef = React.useRef();

  var defaultImageTransform = function defaultImageTransform() {
    return {
      scale: 1,
      translateX: 0,
      translateY: 0,
      config: _objectSpread({}, web.config["default"], {
        precision: 0.01
      })
    };
  };
  /**
   * Animates scale and translate offsets of Image as they change in gestures
   *
   * @see https://www.react-spring.io/docs/hooks/use-spring
   */


  var _useSpring = web.useSpring(function () {
    return _objectSpread({}, defaultImageTransform(), {
      onFrame: function onFrame(f) {
        if (f.scale < 1 || !f.pinching) set(defaultImageTransform); // Prevent dragging image out of viewport

        if (f.scale > 1 && imageIsOutOfBounds(imageRef)) set(defaultImageTransform());
      },
      // Enable dragging in ImagePager if image is at the default size
      onRest: function onRest(f) {
        if (f.scale === 1) setDisableDrag(false);
      }
    });
  }),
      _useSpring2 = _slicedToArray(_useSpring, 2),
      _useSpring2$ = _useSpring2[0],
      scale = _useSpring2$.scale,
      translateX = _useSpring2$.translateX,
      translateY = _useSpring2$.translateY,
      set = _useSpring2[1]; // Reset scale of this image when dragging to new image in ImagePager


  React.useEffect(function () {
    if (!isCurrentImage) set(defaultImageTransform);
  });
  /**
   * Update Image scale and translate offsets during pinch/pan gestures
   *
   * @see https://github.com/react-spring/react-use-gesture#usegesture-hook-supporting-multiple-gestures-at-once
   */

  var bind = reactUseGesture.useGesture({
    onPinch: function onPinch(_ref2) {
      var _ref2$movement = _slicedToArray(_ref2.movement, 1),
          xMovement = _ref2$movement[0],
          _ref2$origin = _slicedToArray(_ref2.origin, 2),
          touchOriginX = _ref2$origin[0],
          touchOriginY = _ref2$origin[1],
          event = _ref2.event,
          ctrlKey = _ref2.ctrlKey,
          last = _ref2.last,
          cancel = _ref2.cancel;

      // Don't calculate new translate offsets on final frame
      if (last) {
        cancel();
        return;
      } // Speed up pinch zoom when using mouse versus touch


      var SCALE_FACTOR = ctrlKey ? 1000 : 250;
      var pinchScale = scale.value + xMovement / SCALE_FACTOR;
      var pinchDelta = pinchScale - scale.value;
      var clientX = event.clientX,
          clientY = event.clientY; // Calculate the amount of x, y translate offset needed to
      // zoom-in to point as image scale grows

      var _getTranslateOffsetsF = getTranslateOffsetsFromScale({
        imageRef: imageRef,
        scale: scale.value,
        pinchDelta: pinchDelta,
        currentTranslate: [translateX.value, translateY.value],
        // Use the [x, y] coords of mouse if a trackpad or ctrl + wheel event
        // Otherwise use touch origin
        touchOrigin: ctrlKey ? [clientX, clientY] : [touchOriginX, touchOriginY]
      }),
          _getTranslateOffsetsF2 = _slicedToArray(_getTranslateOffsetsF, 2),
          newTranslateX = _getTranslateOffsetsF2[0],
          newTranslateY = _getTranslateOffsetsF2[1]; // Restrict the amount of zoom between half and 3x image size


      if (pinchScale < 0.5) set({
        scale: 0.5,
        pinching: true
      });else if (pinchScale > 3.0) set({
        scale: 3.0,
        pinching: true
      });else set({
        scale: pinchScale,
        translateX: newTranslateX,
        translateY: newTranslateY,
        pinching: true
      });
    },
    onPinchEnd: function onPinchEnd() {
      if (scale.value > 1) setDisableDrag(true);else set(defaultImageTransform);
    },
    onDrag: function onDrag(_ref3) {
      var _ref3$movement = _slicedToArray(_ref3.movement, 2),
          xMovement = _ref3$movement[0],
          yMovement = _ref3$movement[1],
          pinching = _ref3.pinching,
          event = _ref3.event,
          cancel = _ref3.cancel,
          first = _ref3.first,
          _ref3$memo = _ref3.memo,
          memo = _ref3$memo === void 0 ? null : _ref3$memo;

      if (event.touches && event.touches.length > 1) return;
      if (pinching || scale.value <= 1) return; // Prevent dragging image out of viewport

      if (scale.value > 1 && imageIsOutOfBounds(imageRef)) cancel();else {
        if (first) {
          return {
            initialTranslateX: translateX.value,
            initialTranslateY: translateY.value
          };
        } // Translate image from dragging


        set({
          translateX: memo.initialTranslateX + xMovement,
          translateY: memo.initialTranslateY + yMovement
        });
        return memo;
      }
    }
  },
  /**
   * useGesture config
   * @see https://github.com/react-spring/react-use-gesture#usegesture-config
   */
  {
    domTarget: imageRef,
    event: {
      passive: false
    }
  });
  /**
   * @see https://github.com/react-spring/react-use-gesture#adding-gestures-to-dom-nodes
   */

  React.useEffect(bind, [bind]); // Handle double-tap on image

  useDoubleClick({
    onDoubleClick: function onDoubleClick(e) {
      // If double-tapped while already zoomed-in, zoom out to default scale
      if (scale.value !== 1) {
        set(defaultImageTransform);
        return;
      } // Zoom-in to origin of click on image


      var touchOriginX = e.clientX,
          touchOriginY = e.clientY;
      var pinchScale = scale.value + 1;
      var pinchDelta = pinchScale - scale.value; // Calculate the amount of x, y translate offset needed to
      // zoom-in to point as image scale grows

      var _getTranslateOffsetsF3 = getTranslateOffsetsFromScale({
        imageRef: imageRef,
        scale: scale.value,
        pinchDelta: pinchDelta,
        currentTranslate: [translateX.value, translateY.value],
        touchOrigin: [touchOriginX, touchOriginY]
      }),
          _getTranslateOffsetsF4 = _slicedToArray(_getTranslateOffsetsF3, 2),
          newTranslateX = _getTranslateOffsetsF4[0],
          newTranslateY = _getTranslateOffsetsF4[1]; // Disable dragging in pager


      setDisableDrag(true);
      set({
        scale: pinchScale,
        translateX: newTranslateX,
        translateY: newTranslateY,
        pinching: true
      });
    },
    ref: imageRef,
    latency: 250
  });
  return React__default.createElement(web.animated.img, {
    ref: imageRef,
    className: "lightbox-image",
    style: _objectSpread({
      transform: web.to([scale, translateX, translateY], function (s, x, y) {
        return "translate(".concat(x, "px, ").concat(y, "px) scale(").concat(s, ")");
      }),
      width: 'auto',
      maxHeight: '100%',
      maxWidth: '100%'
    }, isCurrentImage && {
      willChange: 'transform'
    }),
    src: src,
    alt: alt,
    draggable: "false",
    onDragStart: function onDragStart(e) {
      // Disable image ghost dragging in firefox
      e.preventDefault();
    },
    onClick: function onClick(e) {
      // Don't close lighbox when clicking image
      e.stopPropagation();
      e.nativeEvent.stopImmediatePropagation();
    }
  });
};

/**
 * Gesture controlled surface that animates prev/next page changes via spring physics.
 *
 * @param {array} images Array of image objects to be shown in Lightbox
 * @param {number} currentIndex Index of image in images array that is currently shown
 * @param {function} onPrev True if this image is currently shown in pager, otherwise false
 * @param {function} onNext Function that can be called to disable dragging in the pager
 * @param {function} onClose Function that closes the Lightbox
 *
 * @see https://github.com/react-spring/react-use-gesture
 * @see https://github.com/react-spring/react-spring
 */

var ImagePager = function ImagePager(_ref) {
  var images = _ref.images,
      currentIndex = _ref.currentIndex,
      onPrev = _ref.onPrev,
      onNext = _ref.onNext,
      onClose = _ref.onClose;
  var firstRender = React.useRef(true);
  var imageStageRef = React.useRef(_toConsumableArray(Array(images.length)).map(function () {
    return React__default.createRef();
  }));

  var _useWindowSize = useWindowSize(),
      pageWidth = _useWindowSize.width;

  var _useState = React.useState(false),
      _useState2 = _slicedToArray(_useState, 2),
      disableDrag = _useState2[0],
      setDisableDrag = _useState2[1]; // Generate page positions based on current index


  var getPagePositions = function getPagePositions(i) {
    var down = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : false;
    var xDelta = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : 0;
    var x = (i - currentIndex) * pageWidth + (down ? xDelta : 0);
    if (i < currentIndex - 1 || i > currentIndex + 1) return {
      x: x,
      display: 'none'
    };
    return {
      x: x,
      display: 'block'
    };
  };
  /**
   * Animates translateX of all images at the same time
   *
   * https://www.react-spring.io/docs/hooks/use-springs
   */


  var _useSprings = web.useSprings(images.length, getPagePositions),
      _useSprings2 = _slicedToArray(_useSprings, 2),
      props = _useSprings2[0],
      set = _useSprings2[1]; // Animate page change if currentIndex changes


  React.useEffect(function () {
    // No need to set page position for initial render
    if (firstRender.current) {
      firstRender.current = false;
      return;
    } // Update page positions after prev/next page state change


    set(getPagePositions);
  });
  /**
   * Update each Image's visibility and translateX offset during dragging
   *
   * @see https://github.com/react-spring/react-use-gesture
   */

  var bind = reactUseGesture.useGesture({
    onWheel: function onWheel(_ref2) {
      var distance = _ref2.distance,
          velocity = _ref2.velocity,
          _ref2$direction = _slicedToArray(_ref2.direction, 2),
          xDir = _ref2$direction[0],
          yDir = _ref2$direction[1],
          ctrlKey = _ref2.ctrlKey;

      // Disable drag if Image has been zoomed in to allow for panning
      if (ctrlKey || disableDrag || velocity === 0) return;
      var draggedFarEnough = distance > pageWidth / 3;
      var draggedFastEnough = velocity > 1 && distance <= pageWidth / 3; // Handle next/prev image from valid drag

      if (draggedFarEnough || draggedFastEnough) {
        var goToIndex = clamp(currentIndex + (xDir + yDir > 0 ? -1 : 1), 0, images.length - 1);
        if (goToIndex > currentIndex) onNext();
        if (goToIndex < currentIndex) onPrev();
        if (goToIndex === currentIndex) set(function (i) {
          return getPagePositions(i, false, 0);
        });
      }
    },
    onWheelEnd: function onWheelEnd() {
      set(function (i) {
        return getPagePositions(i, false, 0);
      });
    },
    onDrag: function onDrag(_ref3) {
      var down = _ref3.down,
          _ref3$movement = _slicedToArray(_ref3.movement, 1),
          xMovement = _ref3$movement[0],
          _ref3$direction = _slicedToArray(_ref3.direction, 1),
          xDir = _ref3$direction[0],
          velocity = _ref3.velocity,
          distance = _ref3.distance,
          cancel = _ref3.cancel,
          touches = _ref3.touches;

      // Disable drag if Image has been zoomed in to allow for panning
      if (disableDrag) return;
      var isHorizontalDrag = Math.abs(xDir) > 0.7;
      var draggedFarEnough = down && isHorizontalDrag && distance > pageWidth / 3;
      var draggedFastEnough = down && isHorizontalDrag && velocity > 2.5; // Handle next/prev image from valid drag

      if (draggedFarEnough || draggedFastEnough) {
        var goToIndex = clamp(currentIndex + (xDir > 0 ? -1 : 1), 0, images.length - 1); // Cancel gesture animation

        cancel();
        if (goToIndex > currentIndex) onNext();
        if (goToIndex < currentIndex) onPrev();
      } // Don't move pager during two+ finger touch events, i.e. pinch-zoom


      if (touches > 1) return; // Update page x-coordinates for single finger/mouse gestures

      set(function (i) {
        return getPagePositions(i, down, xMovement);
      });
    }
  },
  /**
   * useGesture config
   * @see https://github.com/react-spring/react-use-gesture#usegesture-config
   */
  {
    domTarget: imageStageRef.current[currentIndex],
    event: {
      passive: true,
      capture: false
    }
  });
  /**
   * @see https://github.com/react-spring/react-use-gesture#adding-gestures-to-dom-nodes
   */

  React.useEffect(bind, [bind, currentIndex]);
  return props.map(function (_ref4, i) {
    var x = _ref4.x,
        display = _ref4.display;
    return React__default.createElement(web.animated.div, {
      ref: imageStageRef.current[i],
      key: i,
      className: "lightbox-image-pager",
      style: {
        display: display,
        transform: x.to(function (xInterp) {
          return "translateX(".concat(xInterp, "px)");
        }),
        position: 'absolute',
        height: '100%',
        width: '100%',
        willChange: 'transform',
        touchAction: 'none'
      }
    }, React__default.createElement("div", {
      role: "presentation",
      className: "lightbox-image-container",
      style: {
        position: 'relative',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: '100%',
        height: '100%',
        touchAction: 'none',
        WebkitUserSelect: 'none',
        MozUserSelect: 'none',
        MsUserSelect: 'none',
        userSelect: 'none'
      } // If the background is clicked, close the lightbox
      ,
      onClick: function onClick() {
        return x.value === 0 && onClose();
      }
    }, React__default.createElement(Image, {
      setDisableDrag: setDisableDrag,
      src: images[i].src,
      alt: images[i].alt,
      isCurrentImage: i === currentIndex
    })));
  });
};

/* eslint-disable no-shadow */
/**
 * Containing element for ImagePager and prev/next button controls
 *
 * @param {array} images Array of image objects to be shown in Lightbox
 * @param {number} currentIndex Index of image in images array that is currently shown
 * @param {function} onPrev True if this image is currently shown in pager, otherwise false
 * @param {function} onNext Function that can be called to disable dragging in the pager
 * @param {function} renderPrevButton A React component that is used for previous button in image pager
 * @param {function} renderNextButton A React component that is used for next button in image pager
 */

var ImageStage = function ImageStage(_ref) {
  var images = _ref.images,
      currentIndex = _ref.currentIndex,
      onPrev = _ref.onPrev,
      onNext = _ref.onNext,
      onClose = _ref.onClose,
      renderPrevButton = _ref.renderPrevButton,
      renderNextButton = _ref.renderNextButton;
  // Extra sanity check that the next/prev image exists before moving to it
  var canPrev = currentIndex > 0;
  var canNext = currentIndex + 1 < images.length;

  var prev = function prev() {
    return canPrev && onPrev();
  };

  var next = function next() {
    return canNext && onNext();
  };

  return React__default.createElement("div", {
    className: "lightbox-image-stage",
    style: {
      flexGrow: 1,
      margin: '25px 0',
      position: 'relative',
      width: '100%',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center'
    }
  }, renderPrevButton({
    canPrev: canPrev
  }), React__default.createElement(ImagePager, {
    images: images,
    currentIndex: currentIndex,
    onClose: onClose,
    onNext: next,
    onPrev: prev
  }), renderNextButton({
    canNext: canNext
  }));
};

function ownKeys$1(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); keys.push.apply(keys, symbols); } return keys; }

function _objectSpread$1(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys$1(source, true).forEach(function (key) { _defineProperty(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys$1(source).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }
/**
 * Animates the lightbox as it opens/closes
 *
 * @param {ReactNode} children All child components of Lightbox
 * @param {boolean} isOpen Flag that dictates if the lightbox is open or closed
 * @param {string} className Classes are applied to the root lightbox component
 * @param {object} style Inline styles are applied to the root lightbox component
 * @param {object} pageTransitionConfig React-Spring useTransition config for page open/close animation
 *
 * @see https://www.react-spring.io/docs/hooks/use-transition
 */

var PageContainer = function PageContainer(_ref) {
  var children = _ref.children,
      isOpen = _ref.isOpen,
      className = _ref.className,
      style = _ref.style,
      pageTransitionConfig = _ref.pageTransitionConfig;
  var defaultTransition = {
    from: {
      transform: 'scale(0.75)',
      opacity: 0
    },
    enter: {
      transform: 'scale(1)',
      opacity: 1
    },
    leave: {
      transform: 'scale(0.75)',
      opacity: 0
    },
    config: _objectSpread$1({}, web.config["default"], {
      mass: 1,
      tension: 320,
      friction: 32
    })
  };
  var transitions = web.useTransition(isOpen, null, merge(defaultTransition, pageTransitionConfig));
  return transitions.map(function (_ref2) {
    var item = _ref2.item,
        key = _ref2.key,
        props = _ref2.props;
    return item && React__default.createElement(web.animated.div, {
      key: key,
      className: "lightbox-container".concat(className ? " ".concat(className) : ''),
      style: _objectSpread$1({}, props, {
        display: 'flex',
        flexDirection: 'column',
        position: 'fixed',
        zIndex: 400,
        top: 0,
        bottom: 0,
        left: 0,
        right: 0
      }, style)
    }, children);
  });
};

/**
 * Creates a SSR + next.js friendly React Portal inside <body />
 *
 * Child components are rendered on the client side only
 *
 * @param {array|node} children Child components will be rendered to the portal
 * @see https://reactjs.org/docs/portals.html
 */
var CreatePortal =
/*#__PURE__*/
function (_React$Component) {
  _inherits(CreatePortal, _React$Component);

  function CreatePortal() {
    var _getPrototypeOf2;

    var _this;

    _classCallCheck(this, CreatePortal);

    for (var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++) {
      args[_key] = arguments[_key];
    }

    _this = _possibleConstructorReturn(this, (_getPrototypeOf2 = _getPrototypeOf(CreatePortal)).call.apply(_getPrototypeOf2, [this].concat(args)));

    _defineProperty(_assertThisInitialized(_this), "preventWheel", function (e) {
      return e.preventDefault();
    });

    return _this;
  }

  _createClass(CreatePortal, [{
    key: "componentDidMount",
    // Only executes on the client-side
    value: function componentDidMount() {
      // Get the document body
      this.body = document.body; // Create a container <div /> for React Portal

      this.portalContainer = document.createElement('div');
      this.portalContainer.setAttribute('class', 'lightbox-portal'); // Append the container to the document body

      this.body.appendChild(this.portalContainer); // Force a re-render as we're on the client side now
      // children prop will render to portalContainer

      this.forceUpdate(); // Add event listener to prevent trackpad/ctrl+mousewheel zooming of lightbox
      // Zooming is handled specifically within /ImageStage/components/Image

      this.portalContainer.addEventListener('wheel', this.preventWheel);
    }
  }, {
    key: "componentWillUnmount",
    value: function componentWillUnmount() {
      // Remove wheel event listener
      this.portalContainer.removeEventListener('wheel', this.preventWheel); // Cleanup Portal from DOM

      this.body.removeChild(this.portalContainer);
    }
  }, {
    key: "render",
    value: function render() {
      // Return null during SSR
      if (this.portalContainer === undefined) return null;
      var children = this.props.children;
      return ReactDOM.createPortal(children, this.portalContainer);
    }
  }]);

  return CreatePortal;
}(React__default.Component);

/**
 * Gesture controlled lightbox that interpolates animations with spring physics.
 *
 * @param {boolean} isOpen Flag that dictates if the lightbox is open or closed
 * @param {function} onClose Function that closes the Lightbox
 * @param {function} onPrev Function that changes currentIndex to previous image in images
 * @param {function} onNext Function that changes currentIndex to next image in images
 * @param {number} currentIndex Index of image in images array that is currently shown
 * @param {function} renderHeader A React component that renders above the image pager
 * @param {function} renderFooter A React component that renders below the image pager
 * @param {function} renderPrevButton A React component that is used for previous button in image pager
 * @param {function} renderNextButton A React component that is used for next button in image pager
 * @param {array} images Array of image objects to be shown in Lightbox
 * @param {string} className Classes are applied to the root lightbox component
 * @param {object} style Inline styles are applied to the root lightbox component
 * @param {object} pageTransitionConfig React-Spring useTransition config for page open/close animation
 *
 * @see https://github.com/react-spring/react-use-gesture
 * @see https://github.com/react-spring/react-spring
 */

var Lightbox = function Lightbox(_ref) {
  var isOpen = _ref.isOpen,
      onClose = _ref.onClose,
      images = _ref.images,
      currentIndex = _ref.currentIndex,
      onPrev = _ref.onPrev,
      onNext = _ref.onNext,
      renderHeader = _ref.renderHeader,
      renderFooter = _ref.renderFooter,
      renderPrevButton = _ref.renderPrevButton,
      renderNextButton = _ref.renderNextButton,
      className = _ref.className,
      style = _ref.style,
      pageTransitionConfig = _ref.pageTransitionConfig;
  // Handle event listeners for keyboard
  React.useEffect(function () {
    /**
     * Prevent keyboard from controlling background page
     * when lightbox is open
     */
    var preventBackgroundScroll = function preventBackgroundScroll(e) {
      var keysToIgnore = ['ArrowUp', 'ArrowDown', 'End', 'Home', 'PageUp', 'PageDown'];
      if (isOpen && keysToIgnore.includes(e.key)) e.preventDefault();
    };
    /**
     * Navigate images with arrow keys, close on Esc key
     */


    var handleKeyboardInput = function handleKeyboardInput(e) {
      if (isOpen) {
        switch (e.key) {
          case 'ArrowLeft':
            onPrev();
            break;

          case 'ArrowRight':
            onNext();
            break;

          case 'Escape':
            onClose();
            break;

          default:
            e.preventDefault();
            break;
        }
      }
    };

    document.addEventListener('keyup', handleKeyboardInput);
    document.addEventListener('keydown', preventBackgroundScroll);
    return function () {
      document.removeEventListener('keyup', handleKeyboardInput);
      document.removeEventListener('keydown', preventBackgroundScroll);
    };
  });
  return React__default.createElement(CreatePortal, null, React__default.createElement(PageContainer, {
    isOpen: isOpen,
    className: className,
    style: style,
    pageTransitionConfig: pageTransitionConfig
  }, renderHeader(), React__default.createElement(ImageStage, {
    images: images,
    onClose: onClose,
    currentIndex: currentIndex,
    onPrev: onPrev,
    onNext: onNext,
    renderPrevButton: renderPrevButton,
    renderNextButton: renderNextButton
  }), renderFooter()));
};

Lightbox.defaultProps = {
  pageTransitionConfig: null,
  className: null,
  style: null,
  renderHeader: function renderHeader() {
    return null;
  },
  renderFooter: function renderFooter() {
    return null;
  },
  renderPrevButton: function renderPrevButton() {
    return null;
  },
  renderNextButton: function renderNextButton() {
    return null;
  }
};

module.exports = Lightbox;
//# sourceMappingURL=index.cjs.js.map



//////////////////
// WEBPACK FOOTER
// ./~/react-spring-lightbox/dist/index.cjs.js
// module id = 136
// module chunks = 0